/**
 * The details Notices and Events share: a title, a message, a day and who it's
 * for, each read and checked with the wizard's own rules. A schema takes these
 * and adds what's its own (Urgent, a start time, a place).
 */
import { NOTICE_TITLE_MAX, NOTICE_TITLE_MIN, eventDayProblem, messageProblem, titleProblem } from "../../notices/noticeRules";
import { NOTICE_BODY_VISIBLE_MAX, NOTICE_BODY_VISIBLE_MIN, noticeBodyVisibleLength } from "../../../utils/noticeBodyHtml";
import { normal } from "../catalogue/findByName";
import { capitalize, quoted } from "../catalogue/wording";
import { isFiller } from "../fillerWords";
import { stripLeadIn } from "../leadInWords";
import { looksLikeQuestion } from "../sideQuestion";
import { dayAsSaid, dayGivenIn, findDay, type DayKey } from "../taskDetails";
import { WEEKDAYS } from "../dateRange";
import { EVERYONE_WORDS, findRecipients, type Recipient } from "./recipients";
import type { DetailRule, Question, TaskContext } from "./taskConversation";

/** The summary shows the start of a long message. */
const MESSAGE_PREVIEW = 140;
export const preview = (message: string) =>
  message.length > MESSAGE_PREVIEW ? `${message.slice(0, MESSAGE_PREVIEW).trimEnd()}…` : message;

/** "Which one do you mean?" offers at most this many. */
const WHICH_ONE_COUNT = 3;

/** What was typed, without the spaces around it. */
export const typed = (said: string) => said.trim();
export const withoutEndPunctuation = (said: string) => typed(said).replace(/[\s?.!,]+$/, "");

/** What the Team member calls who it's for: "who it goes to", "the Circle". */
export const CALLED_WHO = /^(?:recipients?|audience|circle|who(?: it(?:'s| is)? (?:for|going to|goes to)| to send it to)?)$/i;

const TITLE_QUESTION: Question = { text: "What's the title?", choices: [] };
const MESSAGE_QUESTION: Question = { text: "What should it say?", choices: [] };

/** A title the wizard won't take, with the reason, asked again. */
function titleQuestion(title: string, thing: string): Question {
  const called = `What's the ${thing} called?`;
  if (title.length < NOTICE_TITLE_MIN) return { text: `That's a bit short for a title. ${called}`, choices: [] };
  if (title.length > NOTICE_TITLE_MAX) {
    return { text: `That's a bit long for a title: it can be up to ${NOTICE_TITLE_MAX} characters. ${called}`, choices: [] };
  }
  return { text: `${titleProblem(title)}. ${called}`, choices: [] };
}

/** A message the wizard won't take, with the reason, asked again. */
function messageQuestion(message: string): Question {
  const length = noticeBodyVisibleLength(message);
  if (length < NOTICE_BODY_VISIBLE_MIN) {
    return { text: `That's a bit short for a message: it needs at least ${NOTICE_BODY_VISIBLE_MIN} characters. What should it say?`, choices: [] };
  }
  if (length > NOTICE_BODY_VISIBLE_MAX) {
    return {
      text: `That's too long for a message: it can be up to ${NOTICE_BODY_VISIBLE_MAX} characters, and this one has ${length}. What should it say?`,
      choices: [],
    };
  }
  return { text: `${messageProblem(message)}. What should it say?`, choices: [] };
}

/** "skip" or "I'll write it there" when asked what it should say: it's written in the wizard. */
const WRITE_IT_THERE =
  /^(?:skip(?:\s+(?:it|that|this))?|i(?:'ll| will)\s+write\s+it\s+(?:there|in\s+the\s+wizard|myself|later)|let\s+me\s+write\s+it(?:\s+(?:there|myself))?|(?:skip[\s,.]+)?i(?:'ll| will)\s+fill\s+(?:it|that)\s+in(?:\s+(?:there|myself|later|on\s+the\s+(?:next\s+)?screen))?)[\s.!]*$/i;

/**
 * "skip" or "I'll choose on the screen" when asked who it's for: the only way
 * to hand over without an audience. Nothing else counts as skipping it, so a
 * name it doesn't recognise or a question just asks again.
 */
export const CHOOSE_IT_THERE =
  /^(?:skip(?:\s+(?:it|that|this))?|i(?:'ll| will)\s+choose\s+(?:it\s+)?(?:on\s+the\s+(?:screen|next\s+screen)|there|in\s+the\s+wizard|myself|later)|let\s+me\s+(?:choose|pick)\s+(?:it|a\s+circle|the\s+audience)(?:\s+(?:there|myself|later|on\s+the\s+(?:next\s+)?screen))?|(?:skip[\s,.]+)?i(?:'ll| will)\s+fill\s+(?:it|that)\s+in(?:\s+(?:there|myself|later|on\s+the\s+(?:next\s+)?screen))?)[\s.!]*$/i;

/**
 * "I told you. Speling Bee…", "ugh fine. next friday", "Darling I told you already! Saturday…": a
 * remark before the answer, said in its own sentence, which isn't part of it.
 */
const REMARK_FIRST =
  /^(?:(?:(?:darling|bestie|sorry|no|nope|ok(?:ay)?|ugh|fine|well|eish|yes|yeah|ja)[\s,]+)*(?:i\s+(?:already\s+|just\s+)?(?:told|gave|said|wrote|typed)(?:\s+(?:you|them|it|that|this))?(?:\s+already)?|ugh(?:\s+fine)?|fine|no|nope|ok(?:ay)?|well|ooh lovely|lovely|oh|as i said|like i said|i literally just said (?:them|it)(?: lol)?|bestie i literally just said (?:them|it)(?: lol)?)[.!,:]+\s+)+/i;

/** The answer without a remark said before it in its own sentence. */
export const withoutRemark = (said: string) => {
  const rest = typed(said).replace(REMARK_FIRST, "");
  return rest || typed(said);
};

/** "information then. title: Grade 7 Farewell", "the title is Spelling Bee": the title, named. */
const TITLE_NAMED = /(?:^|[.!,;]\s*|\s)(?:the\s+)?title(?:\s+is|\s*:)\s+(.+)$/i;

/** The title, asked first: the Notice or Event it's for names it in "What's the Event called?". */
export const titleDetail = (thing: string, alsoReads: string[]): DetailRule => ({
  name: "title",
  required: true,
  called: /^(?:title|heading|name)$/i,
  step: "title",
  checkSideQuestions: true,
  question: () => TITLE_QUESTION,
  read: (said) => {
    const answer = withoutRemark(said);
    const named = answer.match(TITLE_NAMED)?.[1] ?? answer;
    const title = named.replace(/\s+/g, " ").replace(/^["'“‘]+|["'”’]+$/g, "").replace(/[\s.,:;-]+$/, "");
    if (!title) return TITLE_QUESTION;
    return titleProblem(title) ? titleQuestion(title, thing) : { value: capitalize(title) };
  },
  alsoReads,
});

/** The message, kept as written; "skip" leaves it to the writing step. */
export const messageDetail = (alsoReads: string[], alsoReadsWhen?: DetailRule["alsoReadsWhen"]): DetailRule => ({
  name: "message",
  required: true,
  called: /^(?:message|text|wording|body|what it says)$/i,
  step: "content",
  checkSideQuestions: true,
  question: () => MESSAGE_QUESTION,
  read: (said) => {
    const message = typed(said);
    if (!message) return MESSAGE_QUESTION;
    return messageProblem(message) ? messageQuestion(message) : { value: message };
  },
  writtenThere: WRITE_IT_THERE,
  alsoReads,
  ...(alsoReadsWhen ? { alsoReadsWhen } : {}),
  keepsWords: true,
});

const DAY_EXAMPLE = "Try “next Friday” or “14 November”.";

/** A day that has passed can't be used: the wizard's rule for an Event's day, which a Notice's date shares. */
export const dayHasPassed = (day: DayKey, today: Date) => Boolean(eventDayProblem(dayAsSaid(day, today), today));
const PASSED_DAY: Question = { text: `That day has already passed. ${DAY_EXAMPLE}`, choices: [] };

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const MONTH = `(${MONTHS.join("|")}|${MONTHS.map((m) => m.slice(0, 3)).join("|")})`;
const DAY_THEN_MONTH = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH}\\b`, "i");
const MONTH_THEN_DAY = new RegExp(`\\b${MONTH}\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, "i");

/** "31 September", "30 February": a day the month doesn't have, which would otherwise roll into the next month. */
function impossibleDay(said: string, today: Date): Question | undefined {
  const dayFirst = said.match(DAY_THEN_MONTH);
  const monthFirst = said.match(MONTH_THEN_DAY);
  const [day, monthWord] = dayFirst ? [dayFirst[1], dayFirst[2]] : monthFirst ? [monthFirst[2], monthFirst[1]] : [];
  if (!day || !monthWord) return undefined;
  const month = MONTHS.findIndex((m) => m.startsWith(monthWord.toLowerCase().slice(0, 3)));
  const year = Number(said.match(/\b\d{4}\b/)?.[0] ?? today.getFullYear());
  const days = new Date(year, month + 1, 0).getDate();
  if (Number(day) <= days) return undefined;
  const name = MONTHS[month][0].toUpperCase() + MONTHS[month].slice(1);
  const most = month === 1 ? "28 days (29 in a leap year)" : `${days} days`;
  return { text: `${name} has only ${most}. ${DAY_EXAMPLE}`, choices: [] };
}

/** "the 5th", "ugh fine. the 5th": a day of the month, when that's the only one said. */
const ORDINAL_DAY = /\b(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)\b/gi;

/** This month's day, or next month's once it has passed; undefined for "the 31st" in a 30-day month. */
function dayOfMonth(day: number, today: Date): DayKey | undefined {
  const month = day >= today.getDate() ? today.getMonth() : today.getMonth() + 1;
  const date = new Date(today.getFullYear(), month, day);
  if (date.getDate() !== day) return undefined;
  return `date:${String(date.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` as DayKey;
}

/** A day as an answer: one it can read that hasn't passed. */
const NEXT_WEEKDAY_SAID = new RegExp(`\\bnext\\s+(${WEEKDAYS.join("|")})\\b`, "i");
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * "next Friday" said on a Tuesday: this Friday to some, the one after to others. Which one is asked,
 * with both dates. Said on a Friday or later in the week, "next Friday" can only mean next week's.
 */
export const nextWeekdayToCheck = (said: string, today: Date = new Date()): Question | undefined => {
  const found = said.match(NEXT_WEEKDAY_SAID);
  if (!found) return undefined;
  // Monday-first, so Sunday is the end of the week
  const weekPlace = (day: number) => (day === 0 ? 7 : day);
  const wanted = WEEKDAYS.indexOf(found[1].toLowerCase() as (typeof WEEKDAYS)[number]);
  if (weekPlace(wanted) <= weekPlace(today.getDay())) return undefined;
  const ahead = weekPlace(wanted) - weekPlace(today.getDay());
  const named = (offset: number) => {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
    return `${capitalize(WEEKDAYS[date.getDay()])} ${date.getDate()} ${MONTH_NAMES[date.getMonth()]}`;
  };
  const [thisOne, theOneAfter] = [named(ahead), named(ahead + 7)];
  return {
    text: `Do you mean ${thisOne} or ${theOneAfter}?`,
    choices: [thisOne, theOneAfter],
    clarifies: true,
    weekdayChoices: [thisOne, theOneAfter],
  };
};

export const readDay = (said: string, org: TaskContext): { value: DayKey } | Question => {
  const impossible = impossibleDay(said, org.today);
  if (impossible) return impossible;
  const toCheck = nextWeekdayToCheck(said, org.today);
  if (toCheck) return toCheck;
  const ordinals = Array.from(said.matchAll(ORDINAL_DAY));
  const day = findDay(said, org.today) ?? (ordinals.length === 1 ? dayOfMonth(Number(ordinals[0][1]), org.today) : undefined);
  if (!day) return { text: `I couldn't read that day. ${DAY_EXAMPLE}`, choices: [] };
  return dayHasPassed(day, org.today) ? PASSED_DAY : { value: day };
};

/** A day said plainly in an answer to another question; one that has passed is only mentioned ("thanks to everyone who came on 1 October"). */
export const spotDay = (said: string, org: TaskContext) => {
  if (impossibleDay(said, org.today)) return undefined;
  const given = dayGivenIn(said, org.today);
  return given && !dayHasPassed(given.day, org.today) ? { value: given.day, rest: given.rest } : undefined;
};

/** Nothing but a day, perhaps with "actually" or "make it": "on Friday", "actually Monday". */
export const dayAlone = (said: string, today: Date = new Date()) => {
  // "31 September" isn't a day to change to: readDay says why
  if (impossibleDay(said, today)) return undefined;
  const given = dayGivenIn(said, today);
  return given && /^(?:(?:actually|no|ok(?:ay)?|and|make it|it's|it is)[\s,]*)*$/i.test(given.rest) ? given.day : undefined;
};

/** "actually the 23rd", "make it the 23rd instead": a new day of the month, said as a correction. */
const ORDINAL_CORRECTION =
  /^(?:(?:actually|no|ok(?:ay)?|and|make it|change (?:it|the date|the day) to|move it to)[\s,]+)+(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)(?:\s+instead)?[\s.!]*$/i;

/**
 * A day of the month said as a correction, as a day key: this month's, or next month's once it has
 * passed. Only after "actually" or the like, so "3rd place" is never a date.
 */
/** "sorry, the date is wrong, it's the 23rd": the date said to be wrong, then the day it should be. */
const DATE_IS_WRONG =
  /\b(?:date|day)\b[^.?!]*?(?:wrong|incorrect|not right)\b[\s,.!-]*(?:it(?:'s|\s+is|\s+should\s+be)\s+|make\s+it\s+)?(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)[\s.!]*$/i;

export const dayOfMonthCorrection = (said: string, today: Date): DayKey | undefined => {
  const found = said.trim().match(ORDINAL_CORRECTION) ?? said.trim().match(DATE_IS_WRONG);
  // "the 31st" in a 30-day month isn't a day
  return found ? dayOfMonth(Number(found[1]), today) : undefined;
};

/** "send it to Grade 3", "send the poll to Grade 3", "it's for Everyone", "it should go to Alex": only about who it goes to. */
const SEND_IT_TO =
  /^(?:(?:please|actually|and|oh|no|ok(?:ay)?|wait|sorry|can you|could you)[\s,]+)*(?:(?:send|address|post)\s+(?:(?:it|this|that|the\s+(?:notice|poll|event)|my\s+(?:notice|poll|event))\s+)?|(?:make|change)\s+it\s+|(?:can|could)\s+it\s+(?:go\s+)?|it(?:'s|\s+is|\s+should\s+go|\s+goes|\s+needs\s+to\s+go)\s+)(?:to|for)\s+(.+?)(?:\s+instead)?(?:[\s,]+(?:please|pls))?[.!?]*$/i;
/** "to Grade 3" or "for Everyone" alone, which is only about who it goes to when it names someone. */
const TO_ALONE = /^(?:to|for)\s+(.+)$/i;
/** "only Fugazi General", "just Grade 3": the audience, not a title. */
const ONLY_THEM = /^(?:only|just)\s+(.+)$/i;
/** The last "to" or "for" in an answer, unless it's "not for". */
const LAST_TO = /\b(?:to|for)\s+/gi;

/**
 * Who it's for, from these recipients: one of them by name ("Everyone" by any of its
 * names, or the Organization's), "Which one do you mean?" for a name that fits more
 * than one, or `question` again when there's none.
 */
export function readRecipient(
  said: string,
  recipients: Recipient[],
  org: TaskContext,
  question: (text?: string) => Question
): { value: Recipient } | Question {
  const wanted = stripLeadIn(withoutEndPunctuation(said));
  if (!wanted || isFiller(wanted)) return question();
  if (EVERYONE_WORDS.test(normal(wanted)) && !recipients.some((recipient) => recipient.kind === "everyone")) {
    return { ...question("Only an Admin can send to Everyone. Choose one of your Circles instead."), clarifies: true };
  }
  const found = findRecipients(recipients, wanted, org.organizationName);
  if (found.length === 1) return { value: found[0] };
  // "testing pls", "testing. obviously.", "Everyone. publish it now": the name, with more said around it
  const named = found.length === 0 && !looksLikeQuestion(said) ? namedAmongOtherWords(said, recipients, org) : undefined;
  if (named) return { value: named };
  if (found.length > 1) {
    return { text: "Which one do you mean?", choices: found.slice(0, WHICH_ONE_COUNT).map((r) => r.name), clarifies: true };
  }
  // "will this go to every parent??": a question about who it reaches, not a name to look for
  if (looksLikeQuestion(said)) {
    return ABOUT_REACH.test(said)
      ? question(`${WHO_IT_REACHES} ${question().text}`)
      : question(`That sounds like a question for me. I can answer it once this is ready. ${question().text}`);
  }
  // "too many questions. just use sensible defaults": who it goes to is never guessed
  if (LEAVE_IT_TO_YOU.test(said)) return question(`I won't guess who it goes to. ${question().text}`);
  return question(`I couldn't find ${quoted(wanted)}. ${question().text}`);
}

/** "use sensible defaults", "you choose", "whatever you think": leaving a choice to Ask. */
export const LEAVE_IT_TO_YOU =
  /\b(?:(?:sensible|the|your)\s+defaults?|you\s+(?:choose|pick|decide)|whatever\s+you\s+(?:think|want|like)|up\s+to\s+you|too\s+many\s+questions)\b/i;

/** Words said after a name that aren't part of it: "testing pls", "Everyone now". */
const AFTER_A_NAME = /(?:\s+(?:pls|plz|please|obviously|thanks|thank you|ok|okay|lol|asap|right now|now|then|again|only))+$/i;
/** "the testing circle": the word Circle after a name. */
const CIRCLE_WORD = /\s+(?:circle|group)$/i;

/**
 * The one recipient a longer answer names: its first sentence ("testing. obviously."), or the
 * answer without the words around the name ("testing pls", "the testing circle").
 */
function namedAmongOtherWords(said: string, recipients: Recipient[], org: TaskContext): Recipient | undefined {
  const sentences = typed(said)
    .split(/[.!?;]+|\s+(?:then|and then)\s+/i)
    .map((part) => part.trim())
    .filter(Boolean);
  for (const part of sentences) {
    const name = stripLeadIn(part).replace(AFTER_A_NAME, "").replace(CIRCLE_WORD, "").trim();
    if (!name || isFiller(name)) continue;
    const found = findRecipients(recipients, name, org.organizationName);
    if (found.length === 1) return found[0];
    if (found.length > 1) return undefined;
  }
  return undefined;
}

/** "will this go to every parent??", "who gets it?", "who is in testing?": about who it reaches. */
const ABOUT_REACH = /\b(?:go(?:es)?\s+to|reach(?:es)?|gets?|receives?|every\s*(?:one|parent|body)|all\s+(?:the\s+)?parents|spam|see\s+it|who\s+is\s+in|who'?s\s+in)\b/i;

const WHO_IT_REACHES =
  "It reaches only the people you choose here: Everyone in your Organization, or the members of one Circle. Nothing is sent until you press Publish.";

/**
 * Who it's for in an answer to another question: only a Circle or User that's exactly
 * one of these, after the last "to" or "for". "Sports day for the juniors" stays the
 * title, and so does "Thank you to Everyone".
 */
export function spotRecipient(said: string, recipients: Recipient[]) {
  const last = Array.from(said.matchAll(LAST_TO)).pop();
  if (last?.index === undefined || /\bnot\s*$/i.test(said.slice(0, last.index))) return undefined;
  const name = normal(said.slice(last.index + last[0].length));
  const named = recipients.filter((r) => r.kind !== "everyone" && normal(r.name) === name);
  return named.length === 1 ? { value: named[0], rest: said.slice(0, last.index) } : undefined;
}

/** The part of an answer that's only about who it's for ("send it to Grade 3", "for Everyone"), if it is. */
export function recipientInstruction(said: string, recipients: Recipient[], org: TaskContext) {
  const text = typed(said);
  const sent = text.match(SEND_IT_TO);
  if (sent) return sent[1];
  const only = text.match(ONLY_THEM)?.[1];
  if (only && (EVERYONE_WORDS.test(normal(only)) || findRecipients(recipients, only, org.organizationName).length > 0)) {
    return withoutEndPunctuation(only);
  }
  const alone = text.match(TO_ALONE)?.[1];
  const namesSomeone =
    alone && (EVERYONE_WORDS.test(normal(alone)) || findRecipients(recipients, alone, org.organizationName).length > 0);
  return namesSomeone ? withoutEndPunctuation(alone) : undefined;
}

/** "Who should it go to?" with these recipients to tap, or the same choices after `text`. */
export const whoQuestion = (question: string, recipients: Recipient[]) => (text = question): Question => ({
  text,
  choices: recipients.map((r) => r.name),
});

/** Whether all that was said names one of these recipients: "Grade 3", so "not Grade 3" clears it. */
export const namesRecipient = (said: string, recipients: Recipient[], org: TaskContext) => {
  const named = withoutEndPunctuation(said).replace(/^(?:only|just)\s+/i, "");
  return findRecipients(recipients, named, org.organizationName).length > 0;
};
